import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";
import type { MarketplaceOfferStatus, OfferAuditEntry, OfferDeclineReason } from "@/data/contractor/marketplaceOffer";

export type InstallerOfferTab = "ready" | "needs_comp" | "offered" | "accepted" | "declined";
export type InstallerOfferJobTab = Extract<InstallerOfferTab, "ready" | "needs_comp">;
export type InstallerOfferStatusTab = Extract<InstallerOfferTab, "offered" | "accepted" | "declined">;

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export interface InstallerOfferJobRow {
  id: string;
  jobNumber: string;
  customerName: string;
  projectType: string;
  city: string;
  state: string;
  postalCode: string;
  currentStage: string;
  payoutAmount: number;
  eligibleInstallerCount: number;
  offerTab: InstallerOfferJobTab;
  sortAt: string;
}

export interface InstallerOfferRow {
  id: string;
  jobId: string;
  contractorId: string;
  contractorName: string;
  status: MarketplaceOfferStatus;
  sentAt: string;
  sentBy: string;
  expiresAt: string;
  payoutAmountSnapshot?: number;
  respondedAt?: string;
  responseReason?: OfferDeclineReason;
  responseNote?: string;
  audit: OfferAuditEntry[];
  jobNumber: string;
  customerName: string;
  projectType: string;
  city: string;
  state: string;
  postalCode: string;
  offerTab: InstallerOfferStatusTab;
  sortAt: string;
}

export interface InstallerOffersSummary {
  ready: number;
  needsComp: number;
  offered: number;
  accepted: number;
  declined: number;
}

export interface InstallerOffersPageQuery {
  tab: InstallerOfferTab;
  page: number;
  pageSize: number;
  search?: string;
  refreshToken?: number;
}

type InstallerOfferJobViewRow = {
  id?: string | null;
  job_number?: string | null;
  customer_name?: string | null;
  project_type?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  current_stage?: string | null;
  payout_amount?: number | string | null;
  eligible_installer_count?: number | null;
  offer_tab?: InstallerOfferJobTab | null;
  sort_at?: string | null;
};

type InstallerOfferViewRow = {
  id?: string | null;
  job_id?: string | null;
  contractor_id?: string | null;
  contractor_name?: string | null;
  status?: MarketplaceOfferStatus | null;
  sent_at?: string | null;
  sent_by?: string | null;
  expires_at?: string | null;
  payout_amount_snapshot?: number | string | null;
  responded_at?: string | null;
  response_reason?: OfferDeclineReason | null;
  response_note?: string | null;
  audit?: OfferAuditEntry[] | null;
  job_number?: string | null;
  customer_name?: string | null;
  project_type?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  offer_tab?: InstallerOfferStatusTab | null;
  sort_at?: string | null;
};

type InstallerOffersSummaryViewRow = {
  ready?: number | string | null;
  needs_comp?: number | string | null;
  offered?: number | string | null;
  accepted?: number | string | null;
  declined?: number | string | null;
};

const JOB_COLUMNS = [
  "id",
  "job_number",
  "customer_name",
  "project_type",
  "city",
  "state",
  "postal_code",
  "current_stage",
  "payout_amount",
  "eligible_installer_count",
  "offer_tab",
  "sort_at",
].join(",");

const OFFER_COLUMNS = [
  "id",
  "job_id",
  "contractor_id",
  "contractor_name",
  "status",
  "sent_at",
  "sent_by",
  "expires_at",
  "payout_amount_snapshot",
  "responded_at",
  "response_reason",
  "response_note",
  "audit",
  "job_number",
  "customer_name",
  "project_type",
  "city",
  "state",
  "postal_code",
  "offer_tab",
  "sort_at",
].join(",");

const SUMMARY_COLUMNS = "ready,needs_comp,offered,accepted,declined";

function num(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function sanitizedSearchPattern(search: string): string {
  return `%${search.trim().replace(/[%_]/g, "").replace(/,/g, " ")}%`;
}

async function withPageMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  const startedAt = observeQueryStart(key);
  try {
    const result = await run();
    recordQueryMetric({ key, startedAt, status: "success" });
    return result;
  } catch (error) {
    recordQueryMetric({ key, startedAt, status: "error", error });
    throw error;
  }
}

type OfferStatusFilterRequest = {
  eq: (column: string, value: string) => OfferStatusFilterRequest;
  or: (filters: string) => OfferStatusFilterRequest;
};

function applyOfferStatusTabFilter<T extends OfferStatusFilterRequest>(
  request: T,
  tab: InstallerOfferStatusTab,
  nowIso: string,
): T {
  if (tab === "accepted") {
    return request.eq("status", "accepted");
  }
  if (tab === "offered") {
    return request
      .eq("status", "pending")
      .or(`expires_at.is.null,expires_at.gt.${nowIso}`);
  }
  return request.or(
    `status.in.(declined,expired,withdrawn,superseded),and(status.eq.pending,expires_at.lte.${nowIso})`,
  );
}

function mapJob(row: InstallerOfferJobViewRow): InstallerOfferJobRow {
  const id = String(row.id ?? "");
  return {
    id,
    jobNumber: String(row.job_number ?? id),
    customerName: String(row.customer_name ?? "Unknown"),
    projectType: String(row.project_type ?? "Other"),
    city: String(row.city ?? ""),
    state: String(row.state ?? ""),
    postalCode: String(row.postal_code ?? ""),
    currentStage: String(row.current_stage ?? ""),
    payoutAmount: num(row.payout_amount),
    eligibleInstallerCount: Math.max(0, Math.trunc(num(row.eligible_installer_count))),
    offerTab: (row.offer_tab ?? "ready") as InstallerOfferJobTab,
    sortAt: String(row.sort_at ?? ""),
  };
}

function mapOffer(row: InstallerOfferViewRow): InstallerOfferRow {
  const id = String(row.id ?? "");
  const status = (row.status ?? "pending") as MarketplaceOfferStatus;
  const expiresAt = String(row.expires_at ?? "");
  const expiresAtMs = expiresAt ? Date.parse(expiresAt) : Number.NaN;
  const isExpiredPending =
    status === "pending" &&
    Number.isFinite(expiresAtMs) &&
    expiresAtMs <= Date.now();
  const offerTab: InstallerOfferStatusTab =
    status === "accepted"
      ? "accepted"
      : status === "pending" && !isExpiredPending
        ? "offered"
        : "declined";
  return {
    id,
    jobId: String(row.job_id ?? ""),
    contractorId: String(row.contractor_id ?? ""),
    contractorName: String(row.contractor_name ?? row.contractor_id ?? "Unassigned"),
    status: isExpiredPending ? "expired" : status,
    sentAt: String(row.sent_at ?? ""),
    sentBy: String(row.sent_by ?? ""),
    expiresAt,
    payoutAmountSnapshot: row.payout_amount_snapshot == null ? undefined : num(row.payout_amount_snapshot),
    respondedAt: row.responded_at ?? undefined,
    responseReason: row.response_reason ?? undefined,
    responseNote: row.response_note ?? undefined,
    audit: Array.isArray(row.audit) ? row.audit : [],
    jobNumber: String(row.job_number ?? row.job_id ?? ""),
    customerName: String(row.customer_name ?? "Unknown"),
    projectType: String(row.project_type ?? "Other"),
    city: String(row.city ?? ""),
    state: String(row.state ?? ""),
    postalCode: String(row.postal_code ?? ""),
    offerTab,
    sortAt: String(row.sort_at ?? row.sent_at ?? ""),
  };
}

function mapSummary(row: InstallerOffersSummaryViewRow | null): InstallerOffersSummary {
  return {
    ready: Math.max(0, Math.trunc(num(row?.ready))),
    needsComp: Math.max(0, Math.trunc(num(row?.needs_comp))),
    offered: Math.max(0, Math.trunc(num(row?.offered))),
    accepted: Math.max(0, Math.trunc(num(row?.accepted))),
    declined: Math.max(0, Math.trunc(num(row?.declined))),
  };
}

function isJobTab(tab: InstallerOfferTab): tab is InstallerOfferJobTab {
  return tab === "ready" || tab === "needs_comp";
}

export const InstallerOffersPageService = {
  async getPage(query: InstallerOffersPageQuery): Promise<PageResponse<InstallerOfferJobRow | InstallerOfferRow>> {
    return isJobTab(query.tab) ? this.getJobsPage(query) : this.getOffersPage(query);
  },

  async getJobsPage(query: InstallerOffersPageQuery & { tab: InstallerOfferJobTab }): Promise<PageResponse<InstallerOfferJobRow>> {
    const key = `installer-offers.jobs-page.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        const from = Math.max(0, query.page * query.pageSize);
        const to = from + Math.max(1, query.pageSize) - 1;
        let request = supabase
          .from("v_installer_offer_jobs_ui")
          .select(JOB_COLUMNS, { count: "exact" })
          .eq("offer_tab", query.tab)
          .order("sort_at", { ascending: false })
          .order("id", { ascending: true })
          .range(from, to);

        const search = query.search?.trim();
        if (search) {
          const pattern = sanitizedSearchPattern(search);
          request = request.or([
            `customer_name.ilike.${pattern}`,
            `job_number.ilike.${pattern}`,
            `project_type.ilike.${pattern}`,
            `city.ilike.${pattern}`,
            `state.ilike.${pattern}`,
          ].join(","));
        }

        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as InstallerOfferJobViewRow[]).map(mapJob), count: count ?? 0, error: null };
      });
    } catch (error) {
      console.warn("InstallerOffersPageService.getJobsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getOffersPage(query: InstallerOffersPageQuery & { tab: InstallerOfferStatusTab }): Promise<PageResponse<InstallerOfferRow>> {
    const key = `installer-offers.offers-page.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        const from = Math.max(0, query.page * query.pageSize);
        const to = from + Math.max(1, query.pageSize) - 1;
        const nowIso = new Date().toISOString();
        let request = applyOfferStatusTabFilter(
          supabase
            .from("v_installer_offer_offer_rows_ui")
            .select(OFFER_COLUMNS, { count: "exact" })
            .order("sort_at", { ascending: false })
            .order("id", { ascending: true }),
          query.tab,
          nowIso,
        ).range(from, to);

        const search = query.search?.trim();
        if (search) {
          const pattern = sanitizedSearchPattern(search);
          request = request.or([
            `customer_name.ilike.${pattern}`,
            `job_number.ilike.${pattern}`,
            `project_type.ilike.${pattern}`,
            `contractor_name.ilike.${pattern}`,
            `city.ilike.${pattern}`,
            `state.ilike.${pattern}`,
          ].join(","));
        }

        const { data, error, count } = await request;
        if (error) throw error;
        return {
          data: ((data ?? []) as InstallerOfferViewRow[]).map(mapOffer),
          count: count ?? 0,
          error: null,
        };
      });
    } catch (error) {
      console.warn("InstallerOffersPageService.getOffersPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getSummary(refreshToken?: number): Promise<{ data: InstallerOffersSummary; error: unknown | null }> {
    const key = `installer-offers.summary.${hashFilters({ refreshToken })}`;
    try {
      return await withPageMetrics(key, async () => {
        const nowIso = new Date().toISOString();
        const [summaryResult, offeredResult, acceptedResult, declinedResult] = await Promise.all([
          supabase
            .from("v_installer_offers_summary")
            .select(SUMMARY_COLUMNS)
            .maybeSingle(),
          applyOfferStatusTabFilter(
            supabase.from("v_installer_offer_offer_rows_ui").select("id", { count: "exact", head: true }),
            "offered",
            nowIso,
          ),
          applyOfferStatusTabFilter(
            supabase.from("v_installer_offer_offer_rows_ui").select("id", { count: "exact", head: true }),
            "accepted",
            nowIso,
          ),
          applyOfferStatusTabFilter(
            supabase.from("v_installer_offer_offer_rows_ui").select("id", { count: "exact", head: true }),
            "declined",
            nowIso,
          ),
        ]);
        if (summaryResult.error) throw summaryResult.error;
        if (offeredResult.error) throw offeredResult.error;
        if (acceptedResult.error) throw acceptedResult.error;
        if (declinedResult.error) throw declinedResult.error;

        const base = mapSummary(summaryResult.data as InstallerOffersSummaryViewRow | null);
        return {
          data: {
            ...base,
            offered: offeredResult.count ?? 0,
            accepted: acceptedResult.count ?? 0,
            declined: declinedResult.count ?? 0,
          },
          error: null,
        };
      });
    } catch (error) {
      console.warn("InstallerOffersPageService.getSummary failed:", error);
      return { data: mapSummary(null), error };
    }
  },
};
